import axios from "axios";
import { useState, useContext, useEffect, useRef, useCallback } from "react";
import { Post } from "./Post";
import { UserContext } from "../providers/userProvider";
import {
  Container,
  Button,
  TextField,
  Box,
  Backdrop,
  CircularProgress,
} from "@mui/material";

const API_BASE = "https://us-central1-whitesides-family-blog.cloudfunctions.net";

export const Posts = () => {
  const [posts, setPosts] = useState([]);
  const [originalPosts, setOriginalPosts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const sentinelRef = useRef(null);
  const dateRangeRef = useRef(null);
  const { user } = useContext(UserContext);

  useEffect(() => {
    if (user.groupId) {
      getPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.groupId]);

  // Reconnect observer whenever cursor/hasMore/loadingMore changes so the
  // callback always closes over the latest values.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !loadingMore) {
        getPosts(cursor);
      }
    });

    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, cursor]);

  const getComments = async () => {
    try {
      const res = await axios.get(`${API_BASE}/getComments?groupId=${user.groupId}`);
      setComments(res.data.comments);
    } catch (error) {
      console.error(error);
    }
  };

  const getPosts = useCallback(async (cursorValue = null) => {
    const isInitial = cursorValue === null;
    if (isInitial) setLoading(true);
    else setLoadingMore(true);

    try {
      const url = new URL(`${API_BASE}/getPosts`);
      url.searchParams.set("groupId", user.groupId);
      if (cursorValue) url.searchParams.set("cursor", cursorValue);
      if (dateRangeRef.current?.startDate) url.searchParams.set("startDate", dateRangeRef.current.startDate);
      if (dateRangeRef.current?.endDate) url.searchParams.set("endDate", dateRangeRef.current.endDate);

      const res = await axios.get(url.toString());
      const { posts: newPosts, nextCursor } = res.data;

      setOriginalPosts((prev) => {
        const all = isInitial ? newPosts : [...prev, ...newPosts];
        if (search) {
          setPosts(
            all.filter((post) =>
              ""
                .concat(post.title, post.content, post.authorName)
                .toLowerCase()
                .includes(search.toLowerCase())
            )
          );
        } else {
          setPosts(all);
        }
        return all;
      });

      setCursor(nextCursor);
      setHasMore(!!nextCursor);

      if (isInitial) await getComments();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user.groupId, search]);

  const handleUpdatePost = async (postId, content) => {
    try {
      await axios.post(`${API_BASE}/updatePost`, { postId, userId: user.id, content });
      const update = (list) =>
        list.map((p) => (p.id === postId ? { ...p, content } : p));
      setPosts((prev) => update(prev));
      setOriginalPosts((prev) => update(prev));
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await axios.post(`${API_BASE}/deletePost`, { postId, userId: user.id });
      const remove = (list) => list.filter((p) => p.id !== postId);
      setPosts((prev) => remove(prev));
      setOriginalPosts((prev) => remove(prev));
    } catch (error) {
      console.error(error);
    }
  };

  const postComment = async (postId) => {
    try {
      const res = await axios.post(`${API_BASE}/addComment`, {
        postId,
        content: comment,
        groupId: user.groupId,
        authorName: user.fullName || "",
        userId: user.id || null,
      });
      setComments((prev) => [
        ...prev,
        { ...res.data, createdAt: new Date().toISOString() },
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  const searchBlog = (e) => {
    const searchText = e.target.value;
    setSearch(searchText);
    dateRangeRef.current = null;
    setPosts(
      originalPosts.filter((post) =>
        ""
          .concat(post.title, post.content, post.authorName)
          .toLowerCase()
          .includes(searchText.toLowerCase())
      )
    );
  };

  const applyDateFilter = (range) => {
    dateRangeRef.current = range;
    setOriginalPosts([]);
    setPosts([]);
    setCursor(null);
    setHasMore(true);
    getPosts();
  };

  const dateFilters = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();
    const oneWeekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString();

    const handleClearFilter = () => {
      dateRangeRef.current = null;
      setSearch("");
      setOriginalPosts([]);
      setPosts([]);
      setCursor(null);
      setHasMore(true);
      getPosts();
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "row", md: "column" },
          justifyContent: "space-between",
          marginLeft: { xs: "0px", md: "25px" },
          width: { md: "100%" },
        }}
      >
        <Button
          sx={{ marginTop: { md: "10px" } }}
          size="small"
          variant="contained"
          color="primary"
          onClick={handleClearFilter}
        >
          All
        </Button>
        <Button
          sx={{ marginTop: { md: "10px" } }}
          size="small"
          variant="contained"
          color="primary"
          onClick={() => applyDateFilter({ startDate: startOfToday, endDate: endOfToday })}
        >
          Todays Posts
        </Button>
        <Button
          sx={{ marginTop: { md: "10px" } }}
          size="small"
          variant="contained"
          color="primary"
          onClick={() => applyDateFilter({ startDate: oneWeekAgo, endDate: endOfToday })}
        >
          This Weeks Posts
        </Button>
      </Box>
    );
  };

  return (
    <>
      <Container
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row-reverse" },
          maxWidth: "700px !important",
          mx: "auto",
        }}
      >
        <Box>
          <TextField
            color="primary"
            variant="outlined"
            sx={{
              marginTop: "25px",
              marginLeft: { md: "25px" },
              marginBottom: "10px",
              width: "100%",
              background: "white",
              color: "black",
            }}
            onChange={searchBlog}
            value={search}
            id="outlined-basic"
            label="Search"
            placeholder="Search"
          />
          {dateFilters()}
        </Box>
        <Box>
          {posts.map((post) => (
            <Post
              key={post.id}
              id={post.id}
              title={post.title}
              content={post.content}
              photoUrls={post.photoUrls}
              author={post.authorName}
              date={post.createdAt}
              authorId={post.authorId}
              currentUserId={user.id}
              postComment={postComment}
              comment={comment}
              setComment={setComment}
              comments={comments}
              setComments={setComments}
              onUpdate={handleUpdatePost}
              onDelete={handleDeletePost}
            />
          ))}
          <div ref={sentinelRef} style={{ height: 1 }} />
          {loadingMore && (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
      </Container>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};
